import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	type Theme,
	Typography,
	makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateShift } from "./Create/CreateShift";
import { DeleteShift } from "./Delete/DeleteShift";
import { EditShift } from "./Edit/EditShift";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "ag-grid-enterprise";
import {
	ColDef,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useContextMenuMESF } from "../../../../controls";
import { LazyLoading } from "../../../../controls/LazyLoading/LazyLoading";
import { useShiftsCrewsContext } from "../context/ShiftsCrewsContext";
import { ShiftParameters } from "../models/ShiftParameters";
import { useShiftsOptionsFunctions } from "./customHooks/useShiftsOptionsFunctions";
import { useTableData } from "./customHooks/useTableShift";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		"& .ag-icon-menu": {
			display: "none",
		},
		"& .ag-header-cell-label": {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontSize: "0.85rem",
		},

		"& .ag-header-cell-text": {
			textAlign: "center !important",
		},

		"& .ag-checkbox-input-wrapper": {
			margin: "0 10px",
		},
		"& .ag-cell": {
			alignItems: "center",
			padding: "0 !important",
			border: "unset !important",
		},
		"& .ag-header-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		"& .ag-cell-wrapper": {
			textAlign: "center",
		},
		"& .ag-menu-option": {
			cursor: "pointer",
		},
	},
	relative: {
		position: "relative",
	},
	textfield: {
		margin: theme.spacing(2),
	},
	btnModal: {
		display: "flex",
		justifyContent: "flex-end",
	},
}));

type Props = {
	getShiftsCrewsFromAPI: Function;
	isLoading: boolean;
};
export const TableShiftsCrews = ({
	isLoading,
	getShiftsCrewsFromAPI,
}: Props) => {
	const classes = useStyles();
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditShift, setOpenModalEditShift] = useState(false);
	const [openModalDeleteShift, setOpenModalDeleteShift] = useState(false);
	// ag-grid turns this into a DOM node when the grid has no rows, and an empty string leaves it
	// with nothing to build, which throws. The shift list is empty until the first one is created.
	const messageTable =
		'<span class="ag-overlay-no-rows-center">No shift parameters configured</span>';
	const gridRef = useRef<AgGridReact>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);
	const {
		actions: { setShiftTableData },
	} = useShiftsCrewsContext();
	const { getMenuOptions } = useShiftsOptionsFunctions({
		setShiftId: setShiftTableData,
		setShowCreateModal: setOpenModalNew,
		setShowDeleteModal: setOpenModalDeleteShift,
		setShowEditModal: setOpenModalEditShift,
	});
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs } = useTableData({
		setOpenModalEditShift,
		setOpenModalDeleteShift,
		showContextMenu,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return (
			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<LazyLoading />
			</div>
		);
	}, []);

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableShifts");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableShifts",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid
			container
			justifyContent="center"
			alignItems="center"
			className={classes.root}
		>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					Shifts / Crew
				</Typography>
			</Grid>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					margin="dense"
					value={filterValue}
					onChange={(event) => {
						setFilterValue(event.target.value);
						gridApi?.setQuickFilter(event.target.value);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton edge="start" type="submit">
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px" }}
					className={classes.relative}
				>
					<CreateShift
						show={openModalNew}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalNew(false);
							if (shouldUpdate) getShiftsCrewsFromAPI();
						}}
					/>
					<EditShift
						show={openModalEditShift}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalEditShift(false);
							if (shouldUpdate) getShiftsCrewsFromAPI();
						}}
					/>
					<DeleteShift
						show={openModalDeleteShift}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalDeleteShift(false);
							if (shouldUpdate) getShiftsCrewsFromAPI();
						}}
					/>
					<Grid
						item
						md={12}
						xs={12}
						style={{
							height: "62vh",
							margin: "6px 0 10px",
						}}
					>
						<div style={gridStyle} className="ag-theme-balham">
							<AgGridReact
								ref={gridRef}
								onGridReady={(params) => setGridApi(params.api)}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows={true}
								loadingOverlayComponent={loadingOverlayComponent}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								statusBar={{
									statusPanels: [
										{ statusPanel: "agTotalRowCountComponent", align: "left" },
										{
											statusPanel: "agFilteredRowCountComponent",
											align: "left",
										},
									],
								}}
								rowSelection="single"
								overlayNoRowsTemplate={messageTable}
								onRowDoubleClicked={(event) => {
									setShiftTableData(event.data as ShiftParameters);
									setOpenModalEditShift(true);
								}}
							></AgGridReact>
						</div>
					</Grid>
					<Grid container justifyContent="flex-end">
						<Grid item md={2} xs={12} className={classes.btnModal}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setOpenModalNew(!openModalNew)}
								fullWidth
								style={{ marginTop: 10 }}
							>
								NEW SHIFT
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
